import { GlassPopover, GlassPopoverContent, GlassPopoverTrigger } from "@/components/glass-popover";
import { GlassSeparator } from "@/components/glass-separator";
import { GlassButton } from "@/components/ui/glass-button";
import { GlassCardHorizontal, GlassCard, GlassCardContent, GlassCardDescription, GlassCardFooter, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { GlassTooltip, GlassTooltipContent, GlassTooltipTrigger } from "@/components/ui/glass-tooltip";
import { admin } from "../../config/companies";
import type { BillData, CompanyData, TransferData } from "../../config/companies/types";
import { BookX } from "lucide-react";
import { Suspense, useEffect, useState, useMemo, useCallback } from "react";

import * as XLSX from "xlsx";
import { formatPrice, PriceView } from "@/components/ui/priceView";
import { CURRENCIES, fetchGauToSelectedCurrenciesForToday, type ParsedExchangeDataType } from "@/helpers/currency";

const formatNumber = (number: number, currency: string) => {
  if (currency === "TL") {
    currency = "TRY";
  }
  // Add commas to every third digit
  // Also, print in two decimal places
  // Example: 123,456.00
  return new Intl.NumberFormat(currency === "TRY" ? "tr-TR" : "en-US", {
    maximumFractionDigits: 2,
    style: "currency",
    currency: currency,
  }).format(number);
}

type TransferDataExtra = TransferData & { amountGauBilled: number };

const extendTransfersWithBilledGau = (bills?: BillData[], transfers?: TransferData[]):TransferDataExtra[] | undefined => {
  if (!bills || !transfers) {
    return;
  }
  // must traverse from oldest to latest, bills and transfers are both sorted from latest to oldest.
  let bIndex = bills.length - 1;
  let billCovered = 0;
  const extendedTransfers: TransferDataExtra[] = [];
  for (let tIndex = transfers.length - 1; tIndex >= 0; --tIndex) {
    const t = transfers[tIndex]!;
    let amountGauBilled = 0;
    let tRemaining = t.amount;
    // console.log('t', t.amount);
    while (bIndex >= 0) {
      let bill = bills[bIndex]!;
      let billRemaining = bill.billTotalAmount - billCovered;
      let usageFromBill = Math.min(billRemaining, tRemaining);
      // console.log(bIndex, bill.billTotalAmount, billRemaining, usageFromBill);
      amountGauBilled += (usageFromBill / bill.billTotalAmount) * bill.billTotalAmountGau;
      billCovered += usageFromBill;
      tRemaining -= usageFromBill;
      if (tRemaining === 0) {
        break;
      }
      if (billCovered !== bill.billTotalAmount) {
        console.error("Calculation error!");
      }
      --bIndex;
      billCovered = 0;
    }
    if (tRemaining > 0) {
      console.warn('Transfers exceeded bills');
      amountGauBilled += (tRemaining / t.amount) * t.amountGau;
    }
    extendedTransfers.push({
      ...t,
      amountGauBilled
    });
  }
  return extendedTransfers.reverse();
}

const calculatePriceDivider = (bills?: BillData[], transfers?: TransferDataExtra[]) => {
  if (!bills || !transfers) {
    return 0;
  }
  const minBillAmount = Math.min(...bills.map(bill => bill.billTotalAmountGau));
  const minTransferAmount = Math.min(...transfers.map(transfer => transfer.amountGauBilled));
  // we want at least 125px per bill or 12.5px per transfer.
  // const minAmountPerPixel = Math.min(minBillAmount / 125, minTransferAmount / 12.5);
  // we want at least 144 per bill or 4px per transfer.
  const minAmountPerPixel = Math.min(minBillAmount / 144, minTransferAmount / 4);
  // console.log("minAmountPerPixel:", minAmountPerPixel);
  return minAmountPerPixel;
}

type AllPriceSummary = { 
  raw: { [currency:string]: number }, 
  gauRemaining?: number, 
  gauDifference?: number,
  gauDifferencePerCurrency?: { [currency:string]: number },
}
const calculateAllPriceSummary = (bills?: BillData[], 
  transfers?: TransferDataExtra[], 
  exchangeRate?: Partial<ParsedExchangeDataType>): AllPriceSummary => 
{
  const res: AllPriceSummary['raw'] = {};
  if (!bills || !transfers) {
    return {raw:{}};
  }
  let totalBilledGau = 0;
  let totalTransferredGau = 0;
  bills.forEach((bill) => {
    res[bill.billCurrency] = (res[bill.billCurrency] || 0) + bill.billTotalAmount;
    totalBilledGau += bill.billTotalAmountGau;
  });
  transfers.forEach((transfer) => {
    res[transfer.currency] = (res[transfer.currency] || 0) - transfer.amount;
    totalTransferredGau += transfer.amountGau;
  })
  const fullRes: AllPriceSummary = {raw:res};
  if (exchangeRate) {
    let gauRemaining = 0;
    for (const [currency, amount] of Object.entries(res)) {
      const rate: number|undefined = exchangeRate[currency.toUpperCase() as typeof CURRENCIES[number]];
      if (!rate) {
        // UNSUPPORTED CURRENCY FOUND, SKIP THE WHOLE CALCULATION.
        return fullRes;
      }
      // how much gau does the amount correspond to if it was paid today.
      gauRemaining += amount / rate;
    }
    fullRes.gauRemaining = gauRemaining;
    fullRes.gauDifference = totalBilledGau - totalTransferredGau - gauRemaining;
    fullRes.gauDifferencePerCurrency = {};
    for (const currency of Object.keys(res)) {
      const rate: number|undefined = exchangeRate[currency.toUpperCase() as typeof CURRENCIES[number]];
      if (!rate) { return fullRes };  // UNSUPPORTED CURRENCY FOUND, SKIP THE WHOLE CALCULATION IMPOSSIBLE HERE THOUGH.
      fullRes.gauDifferencePerCurrency[currency] = rate * fullRes.gauDifference;
    }
  }
  return fullRes;
}

function autoFitColumns(worksheet: XLSX.WorkSheet) {
  const data = XLSX.utils.sheet_to_json<string[]>(worksheet, { header: 1 });
  
  const colWidths = data.reduce((widths: number[], row) => {
    row.forEach((cell, i) => {
      const len = cell ? String(cell).length : 0;
      widths[i] = Math.max(widths[i] ?? 10, len);
    });
    return widths;
  }, []);

  worksheet["!cols"] = colWidths.map(w => ({ wch: w + 2 })); // +2 for padding
}

let activeExport = false;
const excelExport = (bills?: BillData[], transfers?: TransferData[], metadata?: CompanyData, timezone="GMT+3") => {
  if (!bills || !transfers || !metadata) {
    return;
  }
  if (activeExport) {
    return;
  }
  activeExport = true;

  const billsExport = bills.map(bill => ({
    ["Tarih"]: bill.billDateString,
    ["No"]: bill.billId,
    ["Ettn"]: bill.billEttn,
    ["Tutar"]: bill.billAmount,
    ["KDV Oranı"]: bill.billVatPercentage,
    ["KDV Tutar"]: bill.billVatAmount,
    ["Toplam Tutar"]: bill.billTotalAmount,
    ["Para Birimi"]: bill.billCurrency,
  }));

  const transfersExport = transfers.map(transfer => ({
    ["Tarih"]: transfer.dateString,
    ["Saat"]: transfer.timeString || " ",
    ["Valör Tarihi"]: transfer.dateValorString,
    ["Referans No"]: transfer.referenceNo,
    ["Tutar"]: transfer.amount,
    ["Para Birimi"]: transfer.currency === "TL"? "TRY" : transfer.currency,
    ["Açıklama"]: transfer.description,
  }));

  const billsWorksheet = XLSX.utils.json_to_sheet(billsExport);
  autoFitColumns(billsWorksheet);
  const transfersWorksheet = XLSX.utils.json_to_sheet(transfersExport);
  autoFitColumns(transfersWorksheet);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, billsWorksheet, "Faturalar");
  XLSX.utils.book_append_sheet(workbook, transfersWorksheet, "Transferler");
  
  const dateTimeStr = new Date(
    ((): number => {
      switch(timezone) {
        case "GMT+3":
          return Date.now() + 1000 * 3600 * 3;
        case "UTC":
          return Date.now();
        default:
          throw new Error("Invalid timezone: " + timezone);
      }
    })()
  ).toISOString()
    .slice(0, -5) // remove milliseconds and Z
    .replace(/:/g, "-") // replace : with -
    .replace(/T/g, "") // replace T with _
    + ((): string => {
      switch (timezone) {
        case "GMT+3":
          return "+3";
        case "UTC":
          return "Z";
        default:
          throw new Error("Invalid timezone: " + timezone);
      }
    })();
  // Triggers a file download in the browser
  const fileName = `${dateTimeStr}_${admin.slug}_${metadata.slug}.xls`;
  XLSX.writeFile(workbook, fileName, { bookType: "xls" });
  activeExport = false;
}

export function CompanyTimeline({ metadata, companyKey }: { metadata?: CompanyData, companyKey: string }) {
  const [bills, setBills] = useState<BillData[]>();
  const [transfers, setTransfers] = useState<TransferData[]>([]); // TODO implement transfers data structure.
  const [exchangeRate, setExchangeRate] = useState<Partial<ParsedExchangeDataType>>();
  const transfersExtra = useMemo(() => extendTransfersWithBilledGau(bills, transfers), [bills, transfers]);
  useEffect(() => {
    fetch(`/${companyKey}/bills.json`)
      .then(response => response.json())
      .then(data => setBills(data));
  }, [companyKey]);
  useEffect(() => {
    fetch(`/${companyKey}/transfers.json`)
      .then(response => response.json())
      .then(data => setTransfers(data));
  }, [companyKey]);
  useEffect(() => {
    fetchGauToSelectedCurrenciesForToday()
      .then((response) => setExchangeRate(response))
  }, []);
  const minAmountPerPixel = useMemo(() => calculatePriceDivider(bills, transfersExtra), [bills, transfersExtra]);
  const handleExcelExport = useCallback(() => excelExport(bills, transfers, metadata), [bills, transfers, metadata]);
  const allPriceSummary = useMemo(() => calculateAllPriceSummary(bills, transfersExtra, exchangeRate), [bills, transfers, exchangeRate]);
  if (!bills || !transfersExtra) {
    return <Suspense fallback={<div>Loading...</div>} />;
  };
  return (
    <div>
      <div className="flex items-center justify-center gap-4 text-l text-red-300 -mb-1 -mt-1">
        <h2 className="m-0 p-0"> 
          Kalan: 
          {' '}
          <span className="text-red-400">
            {
              Object.entries(allPriceSummary.raw).map(([currency, amount]) => `${formatNumber(amount, currency)}`).join(' ; ') 
            }
          </span>
          {' '}
          {allPriceSummary.gauRemaining ? (
            <span className="text-[0.7em] block">
              Bugün Ödense Karşılığı:
              {' '}
              <PriceView amount={allPriceSummary.gauRemaining} currency="GAU" smaller />
            </span>
          ) : null}
          {allPriceSummary.gauDifference ? (
            <span className="text-[0.7em] block">
              Kur Farkı:
              {' '}
              <PriceView amount={allPriceSummary.gauDifference} currency="GAU" smaller />
              {allPriceSummary.gauDifferencePerCurrency ? (
                <span className="text-green-200">
                  <>
                    {
                      Object.entries(allPriceSummary.gauDifferencePerCurrency).map(([c,a]) => (
                        <span key={c}>
                          {' = '}
                          <PriceView amount={a} currency={c} smaller />
                        </span>
                      ))
                    }
                  </>
                </span>
              ) : null}
            </span>
          ) : null}
        </h2>
        
        
      </div>
      <div className="h-16 flex items-center justify-center gap-4 text-white text-2xl">
        <h1 className="flex-1 text-right">
          {/* Bills */}
          Faturalar
        </h1>
        {/* <GlassSeparator orientation="vertical"/> */}
        <GlassTooltip>
          <GlassTooltipTrigger asChild>
            <GlassButton size="icon" variant="outline" onClick={handleExcelExport}>
              <BookX  size={128}/>
            </GlassButton>
          </GlassTooltipTrigger>
          <GlassTooltipContent>
            {/* <p>Show timeline <br /> (Bills and Transfers) </p> */}
            <p>Excel çıktısı al <br /> (Faturalar ve Transferler) </p>
          </GlassTooltipContent>
        </GlassTooltip>
        <h1 className="flex-1">
          {/* Transfers */}
          Transferler
        </h1>
      </div>
      <div className="flex justify-center items-end w-90">
        <div className="flex flex-col flex-1 items-end">
          {
            bills.map(bill => (
              <GlassCardHorizontal
                key={bill.billId} className="w-full max-w-xl m-0" dir="rtl"
                style={{
                  height: bill.billTotalAmountGau / minAmountPerPixel
                }}
              >
                <GlassCardContent className="flex flex-col py-0 pl-4 pr-3 gap-0 justify-center" dir="ltr">
                  <GlassCardDescription className="text-right">
                    {bill.billDateString}
                  </GlassCardDescription>
                  <GlassCardDescription className="text-right text-blue-300">
                    <a href={`${companyKey}/bills/${bill.pdfPath}`} target="_blank" rel="noopener noreferrer" className="underline">
                      {bill.billId}
                    </a>
                  </GlassCardDescription>
                  <GlassSeparator orientation="horizontal" className="my-1"/>
                  <GlassCardDescription className="text-right">
                    {formatNumber(bill.billAmount, bill.billCurrency)}
                  </GlassCardDescription>
                  <GlassCardDescription className="text-right">
                   +({bill.billVatPercentage}%) {formatNumber(bill.billVatAmount, bill.billCurrency)}
                  </GlassCardDescription>
                  <GlassSeparator orientation="horizontal" className="my-1"/>
                  <GlassCardDescription className="text-right">
                    <PriceView amount={bill.billTotalAmount} currency={bill.billCurrency} />
                  </GlassCardDescription>
                  <GlassCardDescription className="text-right">
                    <PriceView amount={bill.billTotalAmountGau} currency="GAU" />
                  </GlassCardDescription>
                </GlassCardContent>
              </GlassCardHorizontal>
            ))
          }
        </div>
        <div className="flex flex-col flex-1 items-begin">
          {
            transfersExtra.map(transfer => (
              <GlassPopover key={transfer.referenceNo}>
                <GlassPopoverTrigger asChild>
                  <GlassCardHorizontal  className="w-full max-w-xl m-0 flex-row"
                    style={{
                      height: transfer.amountGauBilled / minAmountPerPixel,
                    }}
                  >
                    <GlassCardContent className="flex flex-col py-0 pl-1 pr-0 gap-0 justify-center" dir="ltr">
                      <GlassCardDescription className="whitespace-nowrap  text-[0.7em]">
                        {transfer.dateString}
                        {' '}
                        <PriceView amount={transfer.amount} currency={transfer.currency} smaller />
                        {' '}
                        <PriceView amount={transfer.amountGau} currency="GAU" smaller />
                        {/* <PriceView amount={transfer.amountGauBilled} currency="GAU" smaller /> */}
                      </GlassCardDescription>
                    </GlassCardContent>
                  </GlassCardHorizontal>
                </GlassPopoverTrigger>
                <GlassPopoverContent>
                  {transfer.dateString} {transfer.timeString || ''}
                  <br />
                  {transfer.description}
                  <br />
                  Referans Numarası: {transfer.referenceNo}
                  <br />
                  Tutar: {formatPrice(transfer.amount, transfer.currency).join('')}
                  <br />
                  {formatPrice(transfer.amountGau, "GAU").join('')} Gram Altın
                </GlassPopoverContent>
              </GlassPopover>
            ))
          }
        </div>
      </div>
    </div>
  );
};